import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { formatDistanceToNow } from 'date-fns';

interface TokenStats {
  current: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    total_cost: number;
    remaining_tokens: number;
  };
  limits: {
    max_tokens: number;
    max_input_tokens: number;
    max_output_tokens: number;
    input_token_price: number;
    output_token_price: number;
  };
  daily_usage: Array<{
    date: string;
    input_tokens: number;
    output_tokens: number;
    cost: number;
  }>;
  pricing_example: {
    input_tokens: number;
    output_tokens: number;
    cost: string;
  };
}

export function ClaudeTokenStats() {
  const [stats, setStats] = useState<TokenStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTokenStats() {
      try {
        const response = await fetch('/functions/api/claude/stats');
        
        // Log raw response for debugging
        const rawText = await response.text();
        console.log('Raw response:', rawText);
        
        if (!response.ok) {
          console.error('Response error:', rawText);
          throw new Error(`Failed to fetch token stats: ${response.status}`);
        }
        
        let data;
        try {
          data = JSON.parse(rawText);
        } catch (parseError) {
          console.error('JSON parsing error:', parseError);
          console.error('Raw response text:', rawText);
          throw new Error('Failed to parse JSON response');
        }
        
        // Validate response structure with more detailed logging
        if (!data.current || !data.limits) {
          console.error('Invalid response structure:', data);
          throw new Error('Invalid stats response format');
        }
        
        // Provide default values for missing fields
        const safeStats: TokenStats = {
          current: {
            input_tokens: data.current.input_tokens || 0,
            output_tokens: data.current.output_tokens || 0,
            total_tokens: data.current.total_tokens || 0,
            total_cost: data.current.total_cost || 0,
            remaining_tokens: data.current.remaining_tokens || 0
          },
          limits: {
            max_tokens: data.limits.max_tokens || 10_000_000,
            max_input_tokens: data.limits.max_input_tokens || 3000,
            max_output_tokens: data.limits.max_output_tokens || 1000,
            input_token_price: data.limits.input_token_price || 0.00000388,
            output_token_price: data.limits.output_token_price || 0.00000900
          },
          daily_usage: data.daily_usage || [],
          pricing_example: data.pricing_example || {
            input_tokens: 0,
            output_tokens: 0,
            cost: '0.00'
          }
        };
        
        setStats(safeStats);
        setError(null);
      } catch (error) {
        console.error('Comprehensive error fetching token stats:', {
          error,
          timestamp: new Date().toISOString()
        });
        setError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    }

    fetchTokenStats();
    // Refresh every 5 minutes
    const interval = setInterval(fetchTokenStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Claude API Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-2 bg-muted rounded"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Error Loading Claude API Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const usagePercentage = (stats.current.total_tokens / stats.limits.max_tokens) * 100;
  const lastUpdated = formatDistanceToNow(new Date(), { addSuffix: true });

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Claude API Usage</span>
          <span className="text-xs font-normal text-muted-foreground">
            {stats.current.total_tokens.toLocaleString()} / {stats.limits.max_tokens.toLocaleString()} tokens
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Input Tokens: {stats.current.input_tokens.toLocaleString()}</span>
            <span>Output Tokens: {stats.current.output_tokens.toLocaleString()}</span>
          </div>
          <Progress value={usagePercentage} />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Remaining Tokens: {stats.current.remaining_tokens.toLocaleString()}</span>
            <span>Total Cost: ${stats.current.total_cost.toFixed(2)}</span>
          </div>
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Daily Usage: {stats.daily_usage.length} days</span>
          <span>Updated {lastUpdated}</span>
        </div>
      </CardContent>
    </Card>
  );
}